import { useTranslation } from 'react-i18next';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'clsx';

import {
  IconRcVoteTriangle,
  IconRcVoteUsd,
  IconRcVoteDate,
  IconVoteStatusPending,
  IconVoteStatusReject,
  IconVoteStatusResolved,
  IconRcVoteComment,
} from '@/assets/proposals';
import { HelperTooltip } from '@/components';
import { useDeviceTypeIsMobile } from '@/hooks';
import { openInDebank, login } from '@/utils';

import { ProposalItem } from '../../types';
import { useVoteProposal } from '../../hooks';
import { RateStar } from '../RateStar';
import style from './index.module.scss';

export const ProposalVote = ({
  data,
  className,
  onVote,
  isLogged,
  source,
  disabled,
}: {
  data: ProposalItem;
  className?: string;
  onVote?: () => void;
  isLogged: boolean;
  disabled?: boolean;
  source: string;
}) => {
  const isMobile = useDeviceTypeIsMobile();

  const [isVote, setIsVote] = useState(data.is_voter);
  const [voteCount, setVoteCount] = useState(data.voter_count);

  useEffect(() => {
    setIsVote(data.is_voter);
    setVoteCount(data.voter_count);
  }, [data.is_voter, data.voter_count]);

  const { mutate } = useVoteProposal({ hasVoted: isVote, source });

  // 没有和 disabled 合并，因为样式不一样
  const isDisabled = useMemo(() => data?.status === 'finished', [data?.status]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || isDisabled) {
        return;
      }

      if (isMobile) {
        openInDebank({ link: `vote/${data.id}` });

        return;
      }

      if (!isLogged) {
        login();

        return;
      }

      mutate(data.id, {
        onSuccess() {
          setIsVote(pre => !pre);
          setVoteCount(pre => (isVote ? (pre > 1 ? pre - 1 : 0) : pre + 1));
          onVote?.();
        },
      });
    },
    [data.id, mutate, isDisabled, isVote, onVote, isLogged, isMobile, disabled],
  );

  return (
    <div className={cx(isMobile && style.mobile)}>
      <div
        className={cx(
          style.proposalVote,
          isVote ? style.isVote : isDisabled ? style.isDisabled : null,
          className,
        )}
        onClick={handleClick}>
        <IconRcVoteTriangle className={style.voteIcon} />
        <div className={style.voteCount}>{voteCount}</div>
      </div>
    </div>
  );
};

export const ProposalStatus = memo(
  ({ data, className }: { data: ProposalItem; className?: string }) => {
    const { t } = useTranslation();

    const isMobile = useDeviceTypeIsMobile();
    return (
      <div className={cx(isMobile && style.mobile)}>
        <div className={cx(style.statusWrap, className)}>
          <div className={style.proposalDateWrap}>
            <HelperTooltip title={t('ProposalMisc.yvtc11')}>
              <div className={style.status}>
                <IconRcVoteDate className={style.statusIcon} /> {data?._date}
              </div>
            </HelperTooltip>
            <HelperTooltip title={t('ProposalMisc.1y3iko9')}>
              <div className={style.status}>
                <IconRcVoteUsd className={style.statusIcon} /> {data?._usdValue}
              </div>
            </HelperTooltip>
          </div>
          {data?.status !== 'created' ? (
            <div className={style.statusProcess}>
              {data?.status === 'processing' ? (
                <div className={cx(style.status, style.process)}>
                  <img
                    src={IconVoteStatusPending}
                    alt="processing icon"
                    className={style.statusIcon}
                  />
                  {t('ProposalMisc.jzn6ac')}
                </div>
              ) : data.score > 0 ? (
                <div className={cx(style.status, style.resolved)}>
                  <img
                    src={IconVoteStatusResolved}
                    alt="resolved icon"
                    className={style.statusIcon}
                  />
                  {t('ProposalMisc.1hch616')}
                </div>
              ) : (
                <div className={cx(style.status, style.rejected)}>
                  <img
                    src={IconVoteStatusReject}
                    alt="rejected icon"
                    className={style.statusIcon}
                  />
                  {t('ProposalMisc.1q1bese')}
                </div>
              )}
              {data?.status === 'processing' ? null : (
                <RateStar rate={data?.score} className={style.status} />
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export const ProposalComment = memo(
  ({ comment, className }: { comment?: ProposalItem['reply_msg']; className?: string }) => {
    const isMobile = useDeviceTypeIsMobile();
    const commentWithHref = useMemo(
      () =>
        comment?.split(' ').map(x =>
          x.startsWith('http') ? (
            <a
              href={x}
              target="_blank"
              className={style.commentLink}
              rel="noopener nofollow noreferrer"
              onClick={e => e.stopPropagation()}>
              {x}
            </a>
          ) : (
            `${x} `
          ),
        ),
      [comment],
    );

    return comment ? (
      <div className={cx(isMobile && style.mobile)}>
        <div className={cx(style.comment, className)}>
          <IconRcVoteComment className={style.statusIcon} />
          <div>{commentWithHref}</div>
        </div>
      </div>
    ) : null;
  },
);
